import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ViolationDetail } from '@/components/ViolationDetail';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { flattenNodes } from '@/lib/node-tree';
import type { NodesResponse } from '@/stores/scope';

/**
 * `/talent/simulation` (人・組織機能 増分⑤ SC-09、UC-14★中核)。
 * 設計: docs/design/v04-people-org-requirements-detail.md §4-4 / §6 (D-636〜D-641/D-646〜D-654)。
 * 要件: docs/design/v04-people-org-requirements.md §15 (UC-14、確度表示形式)。
 *
 * ⚠️ 「できる／できない」の二値を返さない (J-19)。判定の確度 (§15.4) を3行そのまま出す (D-651、<dl>で実装、
 * 整列済みプリフォーマット文字列にはしない)。未確認者は可にも不可にも寄せない。
 * ⚠️ D-650と同型: `release_unit_price` はAPIが返さない (J-20) ため画面にも出さない。
 *
 * 候補者ピッカーは `GET /employees`/`GET /external-persons` (いずれも company_admin 限定) を
 * 使わず、`GET /talent/skills` (skill:view scope、requiresNonSelfScope と同じ配下メンバー一覧) を
 * 使う。placement_simulation:view は R-COMPANY(all_stores)/R-MANAGER(own_store) のみに付与されており
 * (migration 0141)、own_store の店長が候補者を検索できる経路が company_admin 限定 API しかないと
 * 使い物にならないため (TalentCertificationsPage.tsx と同じ判断)。外部人材は company_admin 限定の
 * ままなので、取得できない場合はセクション自体を出さずに縮退する (グレースフルデグレード、
 * PlacementConstraintsPage.tsx の対象者ピッカーと同じ方針)。
 */

type FaceCoverageRow = {
  face_id: string; business_function_id: string | null; abstraction_level: string | null;
  required_scale_level_id: string | null; confirmed_count: number; unconfirmed_count: number;
  undeterminable: null | 'scale_not_assigned' | 'face_disabled';
};
type MatrixSkill = { skill_id: string; label: string; business_function_id: string | null; abstraction_level: string | null; is_enabled: boolean };
type BusinessFunction = { business_function_id: string; label: string };
type MatrixResponse = { business_functions: BusinessFunction[]; skills: MatrixSkill[] };
type SkillLevelMaster = { skill_level_id: string; label: string; sort_order: number };
type TalentEmployee = { employee_id: string; display_name: string; home_node_id: string; home_node_name: string };
type ExternalPersonListItem = { external_person_id: string; family_name: string; given_name: string };

type UnconfirmedReason = 'no_certification' | 'draft_only' | 'constraint_unconfirmed' | 'review_overdue' | 'legal_undeterminable';
type LegalGuardViolation = { kind: string; detail: Record<string, unknown> | null };
type SimulationResult = {
  target: { node_id: string; faces: FaceCoverageRow[]; notice: 'constraints_not_registered' | null };
  source: { node_id: string; before: FaceCoverageRow[]; after: FaceCoverageRow[] }[];
  confidence: {
    confirmed_only: { covered: number; required: number };
    with_unconfirmed: { covered: number; required: number };
    unconfirmed_persons: { person_key: string; reasons: UnconfirmedReason[] }[];
  };
  blockers: { person_key: string; kind: 'legal'; violations: LegalGuardViolation[] }[];
  resolutions: { constraint_id: string; release_method: 'money' | 'ops' | 'consent' | 'none'; note: string | null }[];
};

type SelectedCandidate = { key: string; employeeId: string | null; externalPersonId: string | null; displayName: string };
// D-720 (2026-09-06): 抽象度は固定3値からテナント別マスタへ移行 (§13-3)。
type AbstractionLevel = { code: string; label: string; sort_order: number };

const UNDETERMINABLE_LABELS: Record<'scale_not_assigned' | 'face_disabled', string> = {
  scale_not_assigned: '尺度未設定のため判定不能', face_disabled: '無効化されたスキルのため判定不能',
};
const UNCONFIRMED_REASON_LABELS: Record<UnconfirmedReason, string> = {
  no_certification: '認定なし', draft_only: '未確定 (draft) の認定のみ',
  constraint_unconfirmed: '配置制約が未確認', review_overdue: '配置制約の棚卸し期限切れ',
  legal_undeterminable: '生年月日未登録のため法令ガード判定不能',
};
const VIOLATION_KIND_LABELS: Record<string, string> = {
  minor_night_work: '年少者の深夜業', minor_overtime: '年少者の時間外労働',
  minor_holiday_work: '年少者の休日労働', exemption_violation: '免除請求への抵触',
};
const RELEASE_METHOD_LABELS: Record<'money' | 'ops' | 'consent' | 'none', string> = {
  money: '金銭', ops: '運用調整', consent: '本人同意', none: 'なし',
};

function FaceCoverageList({ faces, faceLabel, faceMeta, highlightFaceId }: {
  faces: FaceCoverageRow[];
  faceLabel: (faceId: string) => string;
  faceMeta: (faceId: string) => { bfLabel: string | null; levelLabel: string | null };
  /** design-expertレビュー指摘 (Medium): 判断対象の面 (requirement.skill_id) がどれか
   *  一見で分からなかったため、該当行を先頭固定+ハイライトする。 */
  highlightFaceId?: string;
}) {
  if (faces.length === 0) return <p className="text-xs text-text-sub">対象の面がありません。</p>;
  const sorted = highlightFaceId
    ? [...faces].sort((a, b) => (a.face_id === highlightFaceId ? -1 : b.face_id === highlightFaceId ? 1 : 0))
    : faces;
  return (
    <ul className="space-y-1.5">
      {sorted.map((f) => {
        const meta = faceMeta(f.face_id);
        const isHighlighted = f.face_id === highlightFaceId;
        return (
          <li key={f.face_id}
            className={`border rounded p-2 flex items-center justify-between gap-2 ${isHighlighted ? 'border-chrome-base bg-chrome-base/5' : 'border-surface-line'}`}>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-text-body break-words">
                {isHighlighted && <span className="text-chrome-base">▸ </span>}
                {faceLabel(f.face_id)}
              </div>
              {meta.bfLabel && meta.levelLabel && <div className="text-[10px] text-text-sub">{meta.bfLabel} / {meta.levelLabel}</div>}
            </div>
            <div className="shrink-0 text-right">
              <div className="text-sm font-semibold text-text-body whitespace-nowrap">{f.confirmed_count}名</div>
              {f.unconfirmed_count > 0 && <div className="text-[10px] text-text-sub whitespace-nowrap">(未確認 {f.unconfirmed_count}名)</div>}
              {f.undeterminable && <div className="text-[10px] text-alert-text whitespace-nowrap">{UNDETERMINABLE_LABELS[f.undeterminable]}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function PlacementSimulationPage() {
  const user = useAuthStore((s) => s.user);

  const [targetNodeId, setTargetNodeId] = useState('');
  const [skillId, setSkillId] = useState('');
  const [minSkillLevelId, setMinSkillLevelId] = useState('');
  const [requiredCount, setRequiredCount] = useState('1');
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [candidates, setCandidates] = useState<SelectedCandidate[]>([]);
  const [candidateQuery, setCandidateQuery] = useState('');
  const [result, setResult] = useState<SimulationResult | null>(null);

  const { data: nodesData, isLoading: nodesLoading, isError: nodesError } = useQuery<NodesResponse>({
    queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes'),
  });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  // D-636と同じ理由 (CompetencyMatrixPage.tsxと同一判断): own_store/node_scope ユーザーは
  // user.stores に含まれるノードだけを選択肢にし、選んだ瞬間403になるデッドエンドを避ける。
  const selectableNodes = useMemo(() => (
    user?.stores && user.stores.length > 0
      ? flatNodes.filter((n) => user.stores!.includes(n.node.node_id))
      : flatNodes
  ), [flatNodes, user?.stores]);

  const matrixNodeId = targetNodeId || selectableNodes[0]?.node.node_id || '';
  const { data: matrixData } = useQuery<MatrixResponse>({
    queryKey: ['competency-matrix-labels', matrixNodeId],
    queryFn: () => apiFetch<MatrixResponse>(`/competency/matrix?node_id=${encodeURIComponent(matrixNodeId)}`),
    enabled: !!matrixNodeId,
  });
  const { data: skillLevels = [] } = useQuery<SkillLevelMaster[]>({ queryKey: ['skill-levels'], queryFn: () => apiFetch('/masters/skill-levels') });
  const sortedSkillLevels = [...skillLevels].sort((a, b) => a.sort_order - b.sort_order);
  const { data: abstractionLevelsRaw = [] } = useQuery<AbstractionLevel[]>({ queryKey: ['abstraction-levels'], queryFn: () => apiFetch('/masters/abstraction-levels') });
  const abstractionLevelLabelByCode = new Map(abstractionLevelsRaw.map((a) => [a.code, a.label]));

  const bfLabelById = new Map((matrixData?.business_functions ?? []).map((bf) => [bf.business_function_id, bf.label]));
  const skillById = new Map((matrixData?.skills ?? []).map((s) => [s.skill_id, s]));
  const requirableSkills = (matrixData?.skills ?? []).filter((s) => s.is_enabled && s.business_function_id && s.abstraction_level);
  const faceLabel = (faceId: string) => skillById.get(faceId)?.label ?? faceId;
  const faceMeta = (faceId: string) => {
    const s = skillById.get(faceId);
    const bfLabel = s?.business_function_id ? bfLabelById.get(s.business_function_id) ?? null : null;
    const levelLabel = s?.abstraction_level ? abstractionLevelLabelByCode.get(s.abstraction_level) ?? s.abstraction_level : null;
    return { bfLabel, levelLabel };
  };

  // 候補者ピッカー: 配下メンバー (skill:view scope) を使う (own_store の店長でも呼べる、コメント参照)。
  const { data: talentData, isError: talentError } = useQuery<{ employees: TalentEmployee[] }>({
    queryKey: ['talent-skills-for-simulation'], queryFn: () => apiFetch('/talent/skills'), retry: false,
  });
  const employeeCandidates = talentData?.employees ?? [];
  // 外部人材は company_admin 限定 (isCompanyAdmin) のため、403の場合は静かにセクションを消す。
  const { data: externalPersons = [], isError: externalError } = useQuery<ExternalPersonListItem[]>({
    queryKey: ['external-persons-for-simulation'], queryFn: () => apiFetch('/external-persons'), retry: false,
  });
  const showExternalSection = !externalError && externalPersons.length > 0;

  const candidateKeySet = new Set(candidates.map((c) => c.key));
  const normalizedQuery = candidateQuery.trim().toLowerCase();
  const filteredEmployeeCandidates = employeeCandidates.filter((e) => !normalizedQuery || e.display_name.toLowerCase().includes(normalizedQuery));
  const filteredExternalCandidates = externalPersons.filter((p) => {
    const name = `${p.family_name} ${p.given_name}`;
    return !normalizedQuery || name.toLowerCase().includes(normalizedQuery);
  });

  const toggleCandidate = (c: SelectedCandidate) => {
    setCandidates((prev) => {
      if (prev.some((p) => p.key === c.key)) return prev.filter((p) => p.key !== c.key);
      if (prev.length >= 20) { toast.error('候補者は最大20名までです'); return prev; }
      return [...prev, c];
    });
  };

  // QAレビュー指摘 (Minor): 内部ID (person_key、E:xxx/X:xxx) をそのままUIに出さない防御的フォールバック。
  const displayNameOf = (personKey: string) => candidates.find((c) => c.key === personKey)?.displayName ?? '(不明)';
  const nodeName = (nodeId: string) => flatNodes.find((n) => n.node.node_id === nodeId)?.node.name ?? nodeId;

  const simulateMut = useMutation({
    mutationFn: () => apiFetch<SimulationResult>('/placement/simulate', {
      method: 'POST',
      body: JSON.stringify({
        target_node_id: targetNodeId,
        requirement: { skill_id: skillId, min_skill_level_id: minSkillLevelId, required_count: Number(requiredCount) || 1 },
        candidates: candidates.map((c) => (c.employeeId ? { employee_id: c.employeeId } : { external_person_id: c.externalPersonId })),
        period_start_date: periodStartDate,
      }),
    }),
    onSuccess: (res) => { setResult(res); toast.success('シミュレーションを実行しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'シミュレーションの実行に失敗しました'),
  });

  const canSubmit = !!targetNodeId && !!skillId && !!minSkillLevelId && Number(requiredCount) > 0
    && candidates.length > 0 && !!periodStartDate;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-page-title text-text-main">配置シミュレーション</h1>
        <p className="text-sm text-text-sub mt-1">
          候補者を抜き取った場合の対象店舗の充足度と、抜き取り元店舗の充足度変化 (Before/After) を確認します。
          「できる／できない」の判定は返しません。抜き取り元が未充足になっても実行は禁止しません。
        </p>
      </div>

      {nodesLoading ? (
        <div role="status" className="text-text-sub">読み込み中…</div>
      ) : nodesError ? (
        <p role="alert" className="text-sm text-alert-text">組織情報の取得に失敗しました。</p>
      ) : selectableNodes.length === 0 ? (
        <EmptyState message="店舗が見つかりません" description="組織階層が設定されていないか、閲覧できる店舗がありません。" />
      ) : (
        <div className="border border-surface-border rounded-lg bg-white p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="sim-target-node">対象店舗 (新店・欠員補充・異動先)</label>
              <Select value={targetNodeId || undefined} onValueChange={setTargetNodeId}>
                <SelectTrigger id="sim-target-node" aria-label="対象店舗" className="min-h-[44px]"><SelectValue placeholder="選択してください" /></SelectTrigger>
                <SelectContent>
                  {selectableNodes.map(({ node, depth }) => (
                    <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="sim-period-start">開始日</label>
              <input id="sim-period-start" type="date" value={periodStartDate} onChange={(e) => setPeriodStartDate(e.target.value)}
                className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="sim-skill">要件: スキル (面)</label>
              <Select value={skillId || undefined} onValueChange={setSkillId}>
                <SelectTrigger id="sim-skill" aria-label="要件: スキル" className="min-h-[44px]"><SelectValue placeholder="選択してください" /></SelectTrigger>
                <SelectContent>
                  {requirableSkills.map((s) => {
                    const bfLabel = s.business_function_id ? bfLabelById.get(s.business_function_id) : null;
                    const levelLabel = s.abstraction_level ? abstractionLevelLabelByCode.get(s.abstraction_level) ?? s.abstraction_level : '';
                    return <SelectItem key={s.skill_id} value={s.skill_id}>{s.label}{bfLabel ? ` (${bfLabel} / ${levelLabel})` : ''}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="sim-level">要件: 最低習熟度</label>
              <Select value={minSkillLevelId || undefined} onValueChange={setMinSkillLevelId}>
                <SelectTrigger id="sim-level" aria-label="要件: 最低習熟度" className="min-h-[44px]"><SelectValue placeholder="選択してください" /></SelectTrigger>
                <SelectContent>
                  {sortedSkillLevels.map((l) => <SelectItem key={l.skill_level_id} value={l.skill_level_id}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1" htmlFor="sim-count">要件: 必要人数</label>
              <input id="sim-count" type="number" min={1} value={requiredCount} onChange={(e) => setRequiredCount(e.target.value)}
                className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
              <label className="block text-sm text-text-sub" htmlFor="sim-candidate-search">抜き取り候補者 (最大20名)</label>
              <span className="text-xs text-text-sub tabular-nums">{candidates.length} / 20名 選択中</span>
            </div>
            <div className="relative max-w-xs mb-2">
              <Search size={14} aria-hidden className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-sub" />
              <input id="sim-candidate-search" value={candidateQuery} onChange={(e) => setCandidateQuery(e.target.value)}
                placeholder="氏名で検索" className="w-full min-h-[44px] pl-8 pr-3 py-2 rounded border border-surface-border focus-ring" />
            </div>
            {talentError && externalError ? (
              <p className="text-xs text-text-sub">対象者を検索できません。</p>
            ) : (
              <div className="border border-surface-line rounded max-h-64 overflow-y-auto divide-y divide-surface-line">
                {filteredEmployeeCandidates.map((e) => {
                  const key = `E:${e.employee_id}`;
                  return (
                    <label key={key} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface-sunken min-h-[44px]">
                      <input type="checkbox" className="w-4 h-4 accent-chrome-base" checked={candidateKeySet.has(key)}
                        onChange={() => toggleCandidate({ key, employeeId: e.employee_id, externalPersonId: null, displayName: e.display_name })} />
                      <span className="min-w-0 break-words">{e.display_name}</span>
                      <span className="text-[11px] text-text-sub ml-auto shrink-0">{e.home_node_name}</span>
                    </label>
                  );
                })}
                {showExternalSection && filteredExternalCandidates.map((p) => {
                  const key = `X:${p.external_person_id}`;
                  const name = `${p.family_name} ${p.given_name}`;
                  return (
                    <label key={key} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface-sunken min-h-[44px]">
                      <input type="checkbox" className="w-4 h-4 accent-chrome-base" checked={candidateKeySet.has(key)}
                        onChange={() => toggleCandidate({ key, employeeId: null, externalPersonId: p.external_person_id, displayName: name })} />
                      <span className="min-w-0 break-words">{name}</span>
                      <span className="text-[11px] text-text-sub ml-auto shrink-0">外部人材</span>
                    </label>
                  );
                })}
                {filteredEmployeeCandidates.length === 0 && (!showExternalSection || filteredExternalCandidates.length === 0) && (
                  <p className="px-3 py-4 text-xs text-text-sub">該当する対象者がいません。</p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button variant="primary" disabled={!canSubmit || simulateMut.isPending} loading={simulateMut.isPending}
              onClick={() => simulateMut.mutate()}>
              シミュレーションを実行
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="border border-surface-border rounded-lg bg-white p-4">
            <h2 className="text-sm font-semibold text-text-body mb-2">対象店舗の充足度 ({nodeName(result.target.node_id)})</h2>
            {result.target.notice === 'constraints_not_registered' && (
              <p className="text-xs text-text-sub bg-surface-sunken rounded px-2 py-1.5 mb-2">
                この店舗は配置制約が未登録のため、全員が未確認です。
              </p>
            )}
            <FaceCoverageList faces={result.target.faces} faceLabel={faceLabel} faceMeta={faceMeta} highlightFaceId={skillId} />
          </div>

          {result.source.length > 0 && (
            <div className="border border-surface-border rounded-lg bg-white p-4">
              <h2 className="text-sm font-semibold text-text-body mb-3">抜き取り元店舗の充足度変化</h2>
              <div className="space-y-4">
                {result.source.map((s) => (
                  <div key={s.node_id} className="border border-surface-line rounded p-3">
                    <div className="text-xs font-semibold text-text-body mb-2">{nodeName(s.node_id)}</div>
                    {/* D-647: Before/After は≥768pxで並置、<768pxは縦積み (並置しない) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] text-text-sub mb-1">Before</div>
                        <FaceCoverageList faces={s.before} faceLabel={faceLabel} faceMeta={faceMeta} highlightFaceId={skillId} />
                      </div>
                      <div>
                        <div className="text-[11px] text-text-sub mb-1">After</div>
                        <FaceCoverageList faces={s.after} faceLabel={faceLabel} faceMeta={faceMeta} highlightFaceId={skillId} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border border-surface-border rounded-lg bg-white p-4">
            <h2 className="text-sm font-semibold text-text-body mb-2">判定の確度</h2>
            {/* D-651: §15.4の3行形式をそのまま出す (3項目・この順序・丸めない)。<dl>で実装し、
                整列済みのプリフォーマット文字列にはしない (320pxで溢れるため)。 */}
            <dl className="space-y-1.5 text-sm">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <dt className="text-text-sub">確定情報のみでの充足度</dt>
                <dd className="font-semibold text-text-body tabular-nums">
                  {result.confidence.confirmed_only.covered}名 / {result.confidence.confirmed_only.required}名
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <dt className="text-text-sub">未確認者を含めた場合の上限</dt>
                <dd className="font-semibold text-text-body tabular-nums">
                  {result.confidence.with_unconfirmed.covered}名 / {result.confidence.with_unconfirmed.required}名
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <dt className="text-text-sub">判定の確度に影響する未確認者</dt>
                <dd className="font-semibold text-text-body tabular-nums">{result.confidence.unconfirmed_persons.length}名</dd>
              </div>
            </dl>

            {result.confidence.unconfirmed_persons.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {result.confidence.unconfirmed_persons.map((p) => (
                  <li key={p.person_key} className="text-xs border border-surface-line rounded p-2">
                    <span className="font-semibold text-text-body">{displayNameOf(p.person_key)}</span>
                    <span className="text-text-sub"> — {p.reasons.map((r) => UNCONFIRMED_REASON_LABELS[r]).join('、')}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {result.blockers.length > 0 && (
            <div className="border border-danger-border rounded-lg bg-danger-bg p-4">
              <h2 className="text-sm font-semibold text-danger-text mb-2">法令ガードで確認が必要な対象者</h2>
              <ul className="space-y-2">
                {result.blockers.map((b) => (
                  <li key={b.person_key} className="text-xs">
                    <span className="font-semibold text-text-body">{displayNameOf(b.person_key)}</span>
                    <ul className="mt-1 space-y-0.5 pl-3 list-disc">
                      {b.violations.map((v, i) => (
                        <li key={i} className="text-text-body">
                          <span className="font-medium">{VIOLATION_KIND_LABELS[v.kind] ?? v.kind}</span>
                          {v.detail && <span> — <ViolationDetail kind={v.kind} detail={v.detail} /></span>}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.resolutions.length > 0 && (
            <div className="border border-surface-border rounded-lg bg-white p-4">
              <h2 className="text-sm font-semibold text-text-body mb-2">成立させるために解除が必要な制約</h2>
              <ul className="space-y-1.5">
                {result.resolutions.map((r) => (
                  <li key={r.constraint_id} className="text-xs border border-surface-line rounded p-2">
                    解除手段: <span className="font-semibold">{RELEASE_METHOD_LABELS[r.release_method]}</span>
                    {r.note && <span className="text-text-sub"> ({r.note})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
